import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import type { AppDispatch } from '../../store';
import {
  fetchTickets,
  selectAllTickets,
  selectListLoading,
} from '../../store/features/tickets/ticketsSlice';
import AgentTicketsTable from '../../components/tickets/components/AgentTicketsTable';
import { fetchProjects, selectAllProjects } from '../../store/features/projects/projectsSlice';
import { openTicketPortal } from '../../store/features/ticketPortal/ticketPortalSlice';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Heading = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.7rem);
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.heading};
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.1rem;
  color: #9aa4b2;
  font-weight: 700;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StatCard = styled(motion.div)`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StatIcon = styled.span`
  font-size: 1.5rem;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: #e8eaed;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const StatLabel = styled.div`
  color: #9aa4b2;
  font-size: 0.9rem;
  font-weight: 800;
`;

const ContentContainer = styled(motion.div)`
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  color: #e8eaed;
  color-scheme: dark;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(8)};
  color: #cbd2e1;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  opacity: 0.5;
`;

const CURRENT_STATUSES = 'open,in_progress,blocked,resolved';

type UiStatus = 'submitted' | 'in_progress' | 'blocked' | 'resolved' | 'closed';

type TicketsListViewProps = {
  heading: string;
  subtitle: string;
  emptyTitle: string;
  emptyCopy: string;
  queueScope?: 'personal' | 'team';
};

function mapStatusToUi(status?: string): UiStatus {
  switch (status) {
    case 'in_progress':
      return 'in_progress';
    case 'blocked':
      return 'blocked';
    case 'resolved':
      return 'resolved';
    case 'closed':
      return 'closed';
    case 'open':
    default:
      return 'submitted';
  }
}

function tsToDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate() as Date;
  const seconds = value.seconds ?? value._seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function TicketsListView({
  heading,
  subtitle,
  emptyTitle,
  emptyCopy,
  queueScope = 'personal',
}: TicketsListViewProps) {
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const tickets = useSelector(selectAllTickets);
  const loading = useSelector(selectListLoading);
  const projects = useSelector(selectAllProjects);

  const [hydrating, setHydrating] = useState(true);

  const fetchCurrentTickets = useCallback(async () => {
    if (!user?.uid) return;

    setHydrating(true);
    try {
      const params: any = {
        status: CURRENT_STATUSES,
        orderBy: 'plannedEndAt',
        direction: 'asc',
        limit: 200,
      };
      if (queueScope === 'team') {
        params.teamScope = 'assigned';
      } else {
        params.assignedAgentId = user.uid;
      }

      await dispatch(
        fetchTickets(params)
      ).unwrap();
    } finally {
      setHydrating(false);
    }
  }, [dispatch, queueScope, user?.uid]);

  useEffect(() => {
    void dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    void fetchCurrentTickets();
  }, [fetchCurrentTickets]);

  const rows = useMemo(
    () =>
      tickets.map((ticket) => ({
        uiStatus: mapStatusToUi(String(ticket.status)),
        due: tsToDate((ticket as any).plannedEndAt ?? (ticket as any).slaResolutionDueAt),
      })),
    [tickets]
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const inProgress = rows.filter((row) => row.uiStatus === 'in_progress').length;
    const waiting = rows.filter((row) => row.uiStatus === 'blocked').length;
    const dueSoon = rows.filter((row) => {
      const dueTs = row.due?.getTime();
      if (!dueTs) return false;
      const diffDays = Math.ceil((dueTs - Date.now()) / (1000 * 3600 * 24));
      return diffDays >= 0 && diffDays <= 3;
    }).length;

    return { total, inProgress, waiting, dueSoon };
  }, [rows]);

  const openEditor = useCallback((id: string) => {
    dispatch(openTicketPortal({ ticketId: id }));
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusTicketId =
      String(params.get('focusTicket') ?? '').trim() ||
      String(params.get('focus') ?? '').trim();
    if (!focusTicketId) return;

    let cancelled = false;
    (async () => {
      try {
        if (cancelled) return;
        dispatch(openTicketPortal({ ticketId: focusTicketId }));
      } catch (error) {
        console.warn('[AgentTicketsPage] Unable to focus ticket from query param', error);
      } finally {
        if (cancelled) return;
        const next = new URLSearchParams(location.search);
        next.delete('focusTicket');
        next.delete('focus');
        const nextSearch = next.toString();
        navigate(
          {
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
          },
          { replace: true },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.pathname, location.search, navigate]);

  const projectsForTable = useMemo(() => {
    return (projects || [])
      .map((project: any) => {
        const id = project?.id ?? project?.projectId ?? project?.uid ?? project?.key ?? null;
        const name = project?.name ?? project?.title ?? project?.label ?? project?.code ?? '';

        if (!id) return null;
        return { id: String(id), name: String(name) };
      })
      .filter(Boolean) as Array<{ id: string; name?: string }>;
  }, [projects]);

  return (
    <Wrap>
      <Header>
        <Heading>{heading}</Heading>
        <Subtitle>{subtitle}</Subtitle>
      </Header>

      <StatsRow>
        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatHeader>
            <StatIcon>ALL</StatIcon>
          </StatHeader>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Current tickets</StatLabel>
        </StatCard>

        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatHeader>
            <StatIcon>IN</StatIcon>
          </StatHeader>
          <StatNumber>{stats.inProgress}</StatNumber>
          <StatLabel>In progress</StatLabel>
        </StatCard>

        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatHeader>
            <StatIcon>WAIT</StatIcon>
          </StatHeader>
          <StatNumber>{stats.waiting}</StatNumber>
          <StatLabel>Waiting on client</StatLabel>
        </StatCard>

        <StatCard variants={fadeInUp} initial="hidden" animate="visible">
          <StatHeader>
            <StatIcon>DUE</StatIcon>
          </StatHeader>
          <StatNumber>{stats.dueSoon}</StatNumber>
          <StatLabel>Due soon ({'<= 3 days'})</StatLabel>
        </StatCard>
      </StatsRow>

      <ContentContainer variants={fadeInUp} initial="hidden" animate="visible">
        {(!hydrating && !loading && tickets.length === 0) ? (
          <EmptyState>
            <EmptyIcon>--</EmptyIcon>
            <h3>{emptyTitle}</h3>
            <p>{emptyCopy}</p>
          </EmptyState>
        ) : (
          <AgentTicketsTable
            tickets={tickets}
            projects={projectsForTable}
            onOpen={openEditor}
            title="Current Tickets"
            loading={hydrating || loading}
          />
        )}
      </ContentContainer>
    </Wrap>
  );
}

export default function AgentTicketsPage() {
  return (
    <TicketsListView
      heading="List"
      subtitle="Current tickets assigned to your account."
      emptyTitle="No current tickets"
      emptyCopy="All caught up. Check the global queue for new assignments."
    />
  );
}
